// Holdings History: the Portfolio Viewer's table for an analyst book — one column per
// saved set (newest first, grouped by month), asset-class totals and fund rows — with a
// flag on each fund for what changed against the previous set (new, up, down, sold).
import React, { useState } from 'react';
import { API, fmtDate, getAssetClassColor, useApiGet } from './apUtils';
import { BookSelect, Notice } from './apComponents';

const FLAG = {
  new: { text: 'new', cls: 'ap-flag-new', title: 'New holding in this set' },
  up: { text: '▲', cls: 'ap-flag-up', title: 'Weight increased from the previous set' },
  down: { text: '▼', cls: 'ap-flag-down', title: 'Weight reduced from the previous set' },
  exit: { text: 'sold', cls: 'ap-flag-exit', title: 'Held in the previous set, sold in this one' },
};

function Cell({ value, change, holding }) {
  const f = change ? FLAG[change] : null;
  return (
    <td className={`value-col${holding ? ' holding-value' : ''}`}>
      {change === 'exit' ? '' : (value != null ? `${value.toFixed(1)}%` : '-')}
      {f && <span className={`ap-flag ${f.cls}`} title={f.title}>{f.text}</span>}
    </td>
  );
}

export default function HoldingsHistoryTab({ code, setCode, books }) {
  const { data, error, loading } = useApiGet(code ? `${API}/portfolios/${code}/holdings-history` : null);
  const [openClasses, setOpenClasses] = useState({});
  const [openMonths, setOpenMonths] = useState({});
  const toggle = (setter, k) => setter((prev) => ({ ...prev, [k]: !prev[k] }));
  const noteOf = (d) => data?.notes?.[d] || undefined;

  const colsFor = (month) => (openMonths[month.label] ? month.dates : [month.dates[0]]);

  return (
    <div className="tab-panel">
      <BookSelect id="ap-holdings-history-book" value={code}
        onChange={(c) => { setCode(c); setOpenClasses({}); setOpenMonths({}); }} books={books} />
      {error && <Notice kind="error">Failed to load: {error}</Notice>}
      {loading && <div className="chart-loading">Loading holdings history...</div>}
      {data && !data.dates.length && <div className="no-data-message">No holdings saved for this book.</div>}
      {data?.dates?.length > 0 && (
        <div className="holdings-history-container">
          <div className="table-container holdings-history-table-container">
            <table className="holdings-history-table">
              <thead>
                <tr>
                  <th className="name-col sticky-col">Asset Class / Holding</th>
                  {data.months.map((month) => (
                    <React.Fragment key={month.label}>
                      {colsFor(month).map((d, idx) => (
                        <th key={d} title={noteOf(d)}
                          className={idx === 0 ? `month-header${openMonths[month.label] ? ' expanded' : ''}` : 'date-header'}
                          onClick={idx === 0 ? () => toggle(setOpenMonths, month.label) : undefined}>
                          {idx === 0 && month.dates.length > 1 && (
                            <span className="expand-icon">{openMonths[month.label] ? '▼' : '▶'}</span>
                          )}
                          {fmtDate(d)}
                          {idx === 0 && !openMonths[month.label] && month.dates.length > 1 && (
                            <span className="date-count">(+{month.dates.length - 1})</span>
                          )}
                        </th>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.asset_classes.map((ac, i) => (
                  <React.Fragment key={ac.name}>
                    <tr className={`asset-class-row ${openClasses[ac.name] ? 'expanded' : ''}`}
                      onClick={() => toggle(setOpenClasses, ac.name)}>
                      <td className="name-col sticky-col">
                        <span className="expand-icon">{openClasses[ac.name] ? '▼' : '▶'}</span>
                        <span className="asset-class-indicator" style={{ backgroundColor: getAssetClassColor(ac.name, i) }} />
                        {ac.name}
                      </td>
                      {data.months.flatMap((month) => colsFor(month).map((d) => (
                        <td key={d} className="value-col">{ac.totals[d] != null ? `${ac.totals[d].toFixed(1)}%` : '-'}</td>
                      )))}
                    </tr>
                    {openClasses[ac.name] && ac.holdings.map((h) => (
                      <tr key={`${ac.name}-${h.tr_code}`} className="holding-row">
                        <td className="name-col sticky-col holding-name">
                          <span className="holding-indent">└</span>
                          {h.asset_name}
                          {h.apir_code && <span className="apir-code">{h.apir_code}</span>}
                        </td>
                        {data.months.flatMap((month) => colsFor(month).map((d) => (
                          <Cell key={d} value={h.values[d]} change={h.changes[d]} holding />
                        )))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="ap-footnote">
            Each column is a saved set, effective from its date until the next. Flags compare a fund with the
            previous set: <span className="ap-flag ap-flag-new">new</span> <span className="ap-flag ap-flag-up">▲</span> up{' '}
            <span className="ap-flag ap-flag-down">▼</span> down <span className="ap-flag ap-flag-exit">sold</span>.
            Hover a date for the note saved with that set.
          </p>
        </div>
      )}
    </div>
  );
}
